// src/components/dashboard/candidate/ProfileStepsForm.tsx
// Shared 6-step profile form used by both ProfileSetupPage and ProfileJourneyModal.
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import type { CandidateProfileCreatePayload } from '@/lib/models/candidateProfile';

// Step components
import { HoursStep } from './journey-steps/2HourStep';
import { DateStep } from './journey-steps/3DateStep';
import { LocationStep } from './journey-steps/4LocationStep';

const STORAGE_KEY = 'nexilink_profile_steps_progress';
const STORAGE_STEP_KEY = 'nexilink_profile_steps_step';

// #298 — the education step is GONE from first-run. It asked for a university
// and whether you hold a socionomexamen, which is a student's question: after the
// pivot to all job seekers most candidates cannot answer it, and it stood between
// them and the app (App.tsx routes a candidate with no profile straight here).
//
// The fields still exist and stay editable on the profile page — the employer's
// applicant view, the AI CV evaluator and Link's chat context read them. What is
// removed is the DEMAND, not the data. See CANDIDATE_PROFILE_RETHINK.md, whose
// verdict table already called for dropping the student framing; the fuller
// restructuring there is still client-gated and NOT done here.
const STEPS = [
    { component: HoursStep, title: 'Arbetstimmar' },
    { component: DateStep, title: 'Startdatum' },
    { component: LocationStep, title: 'Plats' },
];

const DEFAULT_FORM_DATA: Partial<CandidateProfileCreatePayload> = {
    university: '',
    education_status: undefined,
    other_education: '',
    min_hours_per_week: 15,
    max_hours_per_week: 25,
    available_from_date: '',
    location: '',
};

interface ProfileStepsFormProps {
    /** Called when the user finishes all 6 steps. Receives the profile payload. */
    onComplete: (payload: CandidateProfileCreatePayload) => Promise<void>;
    /** Label for the final submit button (default: "Slutför profil") */
    submitLabel?: string;
    /** Label while submitting (default: "Skapar profil...") */
    submittingLabel?: string;
    /** External error to display */
    error?: string | null;
}

export const ProfileStepsForm: React.FC<ProfileStepsFormProps> = ({
    onComplete,
    submitLabel = 'Slutför profil',
    submittingLabel = 'Skapar profil...',
    error: externalError,
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<Partial<CandidateProfileCreatePayload>>(DEFAULT_FORM_DATA);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasInitialized, setHasInitialized] = useState(false);

    // Restore saved progress
    useEffect(() => {
        if (!hasInitialized) {
            const savedData = localStorage.getItem(STORAGE_KEY);
            const savedStep = localStorage.getItem(STORAGE_STEP_KEY);
            if (savedData && savedStep) {
                try {
                    setFormData(prev => ({ ...prev, ...JSON.parse(savedData) }));
                    setCurrentStep(Math.min(parseInt(savedStep, 10), STEPS.length - 1));
                } catch {
                    localStorage.removeItem(STORAGE_KEY);
                    localStorage.removeItem(STORAGE_STEP_KEY);
                }
            }
            setHasInitialized(true);
        }
    }, [hasInitialized]);

    // Auto-save progress
    useEffect(() => {
        if (hasInitialized) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
            localStorage.setItem(STORAGE_STEP_KEY, currentStep.toString());
        }
    }, [formData, currentStep, hasInitialized]);

    const clearSavedProgress = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_STEP_KEY);
    }, []);

    const updateFormData = (updates: Partial<CandidateProfileCreatePayload>) => {
        setFormData(prev => ({ ...prev, ...updates }));
        setError(null);
    };

    const canProceed = () => {
        // Indices shifted by one when the education step was removed (#298).
        switch (currentStep) {
            case 0: return formData.min_hours_per_week && formData.max_hours_per_week;
            case 1: return formData.available_from_date;
            case 2: return formData.location;
            default: return true;
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);

        try {
            const payload: CandidateProfileCreatePayload = {
                // #298 — sent EMPTY, not 'none'. 'none' is a claim ("I have no
                // education") the user never made; empty is the truth ("not asked").
                // The backend accepts both and every reader already skips empty.
                university: formData.university || '',
                education_status: formData.education_status || '',
                other_education: formData.education_status === 'other' ? (formData.other_education || '') : undefined,
                min_hours_per_week: formData.min_hours_per_week || 15,
                max_hours_per_week: formData.max_hours_per_week || 25,
                available_from_date: formData.available_from_date || '',
                location: formData.location || '',
            };

            await onComplete(payload);
            clearSavedProgress();
        } catch (err: any) {
            setError(err?.message || 'Ett fel uppstod. Försök igen.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        if (!canProceed()) return;
        if (currentStep < STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            handleSubmit();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
            setError(null);
        }
    };

    const CurrentStepComponent = STEPS[currentStep].component;
    const displayError = externalError || error;

    return (
        <>
            {/* Step progress — grid gives every step equal width */}
            <div className="mb-8 relative" style={{ display: 'grid', gridTemplateColumns: `repeat(${STEPS.length}, 1fr)` }}>
                {STEPS.map((step, idx) => (
                    <div key={idx} className="flex flex-col items-center">
                        <div
                            className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                                idx < currentStep
                                    ? 'bg-[#1A4D4F] text-[#f5f5f5]'
                                    : idx === currentStep
                                        ? 'bg-white text-[#1A4D4F] ring-2 ring-[#1A4D4F] shadow-sm'
                                        : 'bg-gray-100 text-gray-400 border border-gray-200'
                            }`}
                        >
                            {idx < currentStep ? '✓' : idx + 1}
                        </div>
                        <span
                            className={`hidden sm:block text-[11px] mt-1.5 text-center ${
                                idx <= currentStep ? 'text-[#1A4D4F] font-medium' : 'text-gray-400'
                            }`}
                        >
                            {step.title}
                        </span>
                    </div>
                ))}
                {/* Connector lines drawn as absolute segments between circle centres */}
                {STEPS.slice(0, -1).map((_, idx) => {
                    // Each column is 1/N wide; centre of column i is at (i + 0.5) / N * 100%
                    const n = STEPS.length;
                    const leftPct = ((idx + 0.5) / n) * 100;
                    const rightPct = 100 - ((idx + 1.5) / n) * 100;
                    return (
                        <div
                            key={`line-${idx}`}
                            className={`absolute top-[18px] h-0.5 rounded-full transition-all duration-500 ${
                                idx < currentStep ? 'bg-[#1A4D4F]' : 'bg-gray-200'
                            }`}
                            style={{ left: `calc(${leftPct}% + 18px)`, right: `calc(${rightPct}% + 18px)` }}
                        />
                    );
                })}
            </div>

            {/* Step content card */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm">
                <div className="p-6 sm:p-8">
                    {displayError && (
                        <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-3">
                            <p className="text-red-700 text-sm">{displayError}</p>
                        </div>
                    )}

                    <CurrentStepComponent
                        formData={formData}
                        updateFormData={updateFormData}
                    />
                </div>

                {/* Navigation footer */}
                <div className="border-t-2 border-gray-100 bg-gray-50 px-6 sm:px-8 py-4 rounded-b-2xl">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 0}
                            className="flex items-center gap-1.5 px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Tillbaka
                        </button>

                        <p className="hidden sm:block text-xs font-medium text-gray-500">
                            {currentStep === STEPS.length - 1 ? (
                                <span className="text-[#1A4D4F]">Sista steget!</span>
                            ) : (
                                <span>{STEPS.length - currentStep - 1} steg kvar</span>
                            )}
                        </p>

                        <button
                            onClick={handleNext}
                            disabled={!canProceed() || isSubmitting}
                            className="flex items-center gap-2 px-5 py-2.5 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-[#f5f5f5] border-t-transparent rounded-full animate-spin" />
                                    {submittingLabel}
                                </>
                            ) : currentStep === STEPS.length - 1 ? (
                                <>
                                    {submitLabel}
                                    <Sparkles className="w-4 h-4" />
                                </>
                            ) : (
                                <>
                                    Fortsätt
                                    <ChevronRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
